/**
 * Treatment Presented vs Accepted — V2 "Explain" copy.
 *
 * PURE DATA ONLY. No Vue, no DOM, no imports — the node-test
 * (cypress/node-tests/tx-presented-v2-help.test.js) loads and executes THIS EXACT
 * FILE rather than mirroring it, which only works while it stays import-free.
 *
 * ── What this is ────────────────────────────────────────────────────────────
 * The panel's Explain toggle turns on a small `?` mark beside every label that
 * has an entry here. Hovering (or tapping) the label shows the entry. One
 * dictionary, one delegated listener, no per-label component — see the CSS in
 * AdvancedTxPlannedV2.scss and the wiring in AdvancedTxPlannedV2.vue.
 *
 * ⛔ This is NOT `src/pages/Huddle/huddleMetricGlossary.js`. That one backs the
 * MetricInfo ⓘ popovers on the MAIN Huddle page and is untouched by this panel.
 * Two systems on purpose: this panel's help is toggled, inline, and V2-owned.
 *
 * ── The rule for editing ────────────────────────────────────────────────────
 * Every sentence here is a claim about how the report computes something. If a
 * calculation changes, the copy changes IN THE SAME COMMIT. A help note that
 * confidently describes the old behaviour is worse than no help at all — the
 * reader has no way to know it is stale.
 *
 * Entries 1–16 are Nilay's Claude.ai design copy, ported verbatim. The last four
 * were written for the features that shipped after that export (Quick sets,
 * Patient acceptance, Who moved forward, recall exclusion) and are checked
 * against the code in `advancedTxPlannedV2Math.js` /
 * `advancedTxPlannedV2QuickSets.js`.
 *
 * Every `anchor` must resolve to a heading in
 * `teamio-docs/src/content/docs/huddle/treatment-presented-vs-accepted.md`.
 * Starlight auto-slugs heading text; there is no custom-heading-id plugin, so
 * `## Heading {#id}` would render the braces as visible text. The node-test
 * cross-checks these anchors against that article when the repo is present.
 */

const DOC_BASE = 'https://docs.teamio.app/huddle/treatment-presented-vs-accepted/';

const HELP = {
  // ==========================================================================
  // The money
  // ==========================================================================
  presented: {
    title: 'Tx planned (presented)',
    body: 'Everything diagnosed and entered in the patient’s chart in this window — Open Dental status TP or C, a fee above zero, and a treatment-planned date inside the timeframe.',
    note: 'Diagnostic and preventive codes are deliberately left out (D0…, D1…), except sealants D1351 and fluoride D1206 / D1208. Every dollar is a net fee: the insurance fee-schedule amount minus discounts, or your office fee minus discounts when there is no insurance fee.',
    anchor: '#what-counts-as-presented'
  },

  accepted: {
    title: 'Accepted',
    body: 'Presented treatment whose Open Dental appointment is Scheduled or Completed. Scheduled plus Completed is your accepted dollars.',
    note: 'A verbal yes is not acceptance. Until the planned procedure is attached to a booked appointment it reads as Unscheduled here — even if the patient signed the plan and left a deposit.',
    anchor: '#what-does-accepted-mean'
  },

  acceptance: {
    title: 'Tx acceptance %',
    body: 'The share of presented dollars that got booked or done.',
    formula: '(accepted $ ÷ presented $) × 100',
    note: 'Measured in dollars, not case counts — ten accepted fillings and one declined implant is not 91%. There is no universal good number; watch your own trend on the same filters.',
    anchor: '#the-formula'
  },

  scheduled: {
    title: 'Scheduled',
    body: 'The slice of presented treatment whose appointment status is Scheduled. It counts as accepted.',
    note: 'A slice of Tx planned, not an addition to it. Scheduled and Completed never overlap.',
    anchor: '#the-three-totals'
  },

  completed: {
    title: 'Completed',
    body: 'The slice whose appointment is Completed — the work is done. It counts as accepted.',
    note: 'A slice of Tx planned, not an addition to it.',
    anchor: '#the-three-totals'
  },

  unscheduled: {
    title: 'Unscheduled',
    body: 'Presented treatment that is not accepted: no appointment attached, sitting on the unscheduled list, broken, or only planned.',
    note: 'This is the follow-up list. Open the patient details, sort by status, and work these rows.',
    anchor: '#the-three-totals'
  },

  subsets: {
    title: 'Why the parts don’t add up',
    body: 'Scheduled and Completed are slices of presented treatment, not additions to it — they only sum to the presented total when acceptance is 100%.',
    note: 'The gap between them is your unaccepted treatment. That gap is the number worth working.',
    anchor: '#scheduled-plus-completed-doesnt-equal-tx-planned'
  },

  monthly: {
    title: 'Month by month',
    body: 'Every figure buckets by the date treatment was planned, not the date it was completed. A crown planned 20 Jan and completed 5 Mar lifts January.',
    note: 'So past months keep improving as old plans finally get booked, and the current month always looks low. Don’t read this month’s number on the 3rd and panic.',
    anchor: '#your-acceptance-percentage-month-by-month'
  },

  category: {
    title: 'By procedure category',
    body: 'The same dollars split by Open Dental procedure category, with a category coloured the same across all three stages.',
    note: 'Slices are sized by dollars, not procedure count — one implant case can outweigh forty fillings.',
    anchor: '#the-three-totals'
  },

  codeMatrix: {
    title: 'Per-code breakdown',
    body: 'The same presented and accepted math for each procedure code you selected, so providers are compared on identical work.',
    anchor: '#questions-this-report-can-answer'
  },

  // ==========================================================================
  // The filters
  // ==========================================================================
  procType: {
    title: 'Procedure type',
    body: 'Your own Open Dental procedure categories. Type and code are an either/or choice — pick one, not both.',
    anchor: '#procedure-type-or-procedure-code'
  },

  procCode: {
    title: 'Procedure code',
    body: 'Narrow to individual procedure codes. Every number on the screen then reflects only those codes.',
    anchor: '#procedure-type-or-procedure-code'
  },

  providers: {
    title: 'Providers',
    body: 'The provider each procedure is charted under — not necessarily the person who presented the plan.',
    anchor: '#providers-and-presenter'
  },

  presenter: {
    title: 'Presenter',
    body: 'The person assigned as presenter on a saved Open Dental treatment plan — useful for comparing how team members do at the same conversation.',
    note: 'Treatment charted without a saved plan, or saved without a presenter, has nothing to filter on.',
    anchor: '#providers-and-presenter'
  },

  timeframe: {
    title: 'Timeframe',
    body: 'Filters on the date treatment was planned. Ask for July and you get everything diagnosed in July, including work completed in September.',
    note: 'No future dates, a single range can’t exceed 5 years, and the earliest date is bounded by how much Open Dental history your site keeps — 4 years by default.',
    anchor: '#timeframe'
  },

  priorities: {
    title: 'Treatment priorities',
    body: 'Include or exclude Open Dental treatment priorities. Exclude is usually the more useful direction.',
    note: 'It’s how you keep Rejected, Wait, and 2nd/3rd/4th alternative plans from dragging down a number meant to measure real presented treatment.',
    anchor: '#treatment-priorities'
  },

  // ==========================================================================
  // Added after the design export — these describe features the mock never saw.
  // Checked against advancedTxPlannedV2QuickSets.js and advancedTxPlannedV2Math.js.
  // ==========================================================================
  quickSets: {
    title: 'Quick sets',
    body: 'Saved bundles of procedure codes, shared by everyone at this office. Click one to drop its codes into the Procedure code filter and re-run the report; click it again to take them back out.',
    note: 'Clicking a second set adds to the first, so you can combine them. Only an Admin can create, rename or delete them — they are shared, so one person tidying up would change the chips for the whole team.',
    anchor: '#quick-sets'
  },

  patientAcceptance: {
    title: 'Patient acceptance',
    body: 'The share of patients who scheduled at least one procedure from what they were shown — counted per patient, not per dollar.',
    formula: '(patients who started ÷ patients presented treatment) × 100',
    note: 'Read it next to Tx acceptance, not instead of it. Ten crowns planned and two booked is 20% by dollars but one patient who is moving forward; a big phased plan drags the dollar number down while this one stays honest.',
    anchor: '#patient-acceptance'
  },

  movedForward: {
    title: 'Who moved forward',
    body: 'Every patient presented treatment, sorted into three groups: they booked all of it, they booked some of it, or they booked none of it. Click any row to see exactly which patients.',
    note: '“Started, not finished” is the group the dollar percentage hides — a patient doing two crowns a month is real progress that reads as a failure in an acceptance number.',
    anchor: '#who-moved-forward'
  },

  recallExcluded: {
    title: 'Why recall visits don’t count',
    body: 'Booking a cleaning is not accepting treatment. Patient acceptance ignores any procedure your office lists as a recall procedure in Open Dental — typically prophy, perio maintenance, fluoride, exams and x-rays.',
    note: 'A patient shown a crown and a root canal who books only their next hygiene visit correctly reads as having started nothing. And a patient whose whole plan was recall work is left out of the percentage altogether — they were never offered treatment to accept.',
    anchor: '#why-recall-visits-dont-count'
  }
};

/** Absolute Help Center URL for a key. Empty string for an unknown key. */
function helpHref(key) {
  const entry = HELP[key];
  if (!entry || !entry.anchor) return '';
  return DOC_BASE + entry.anchor;
}

/** The entry a `data-help` attribute names, or null. Never throws. */
function helpFor(key) {
  return (key && HELP[key]) || null;
}

export { DOC_BASE, HELP, helpHref, helpFor };
